import SharedButton from '@/global/Button/Button';
import React from 'react';

interface FeedbackSurveyLayoutProps {
  title: string;
  subtitle: string;
  currentIndex: number;
  totalSteps: number;
  children: React.ReactNode;
  onBack: () => void;
  onPrimary: () => void;
  backDisabled?: boolean;
  primaryLabel: string;
  primaryDisabled?: boolean;
  primaryLoading?: boolean;
}

const FeedbackSurveyLayout: React.FC<FeedbackSurveyLayoutProps> = ({
  title,
  subtitle,
  currentIndex,
  totalSteps,
  children,
  onBack,
  onPrimary,
  backDisabled = false,
  primaryLabel,
  primaryDisabled = false,
  primaryLoading = false,
}) => {
  const safeTotal = Math.max(totalSteps, 1);
  const pct = Math.min(100, Math.round(((currentIndex + 1) / safeTotal) * 100));

  return (
    <div className="min-h-[calc(100vh-68px)] bg-slate-100 flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-2xl">
        <div className="mb-3 flex items-center justify-between text-xs font-medium text-slate-500">
          <span className="text-teal-700">
            Question {currentIndex + 1} of {safeTotal}
          </span>
          <span>{pct}%</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
          <div
            className="h-full rounded-full bg-gradient-to-r from-teal-500 to-sky-600 transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>

        <div className="rounded-2xl bg-white px-6 py-8 shadow-lg shadow-slate-200/80 ring-1 ring-slate-200/60">
          <header className="mb-8 text-center">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
            <p className="mt-2 text-sm text-slate-500">{subtitle}</p>
          </header>

          <div className="min-h-[200px]">{children}</div>

          <footer className="mt-10 flex items-center justify-between gap-4 border-t border-slate-100 pt-6">
            <button
              type="button"
              onClick={onBack}
              disabled={backDisabled}
              className={`inline-flex items-center gap-1 text-sm font-medium transition-colors ${
                backDisabled ? 'cursor-not-allowed text-slate-300' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <span aria-hidden>←</span> Back
            </button>
            <SharedButton
              variant="primary"
              onClick={onPrimary}
              disabled={primaryDisabled || primaryLoading}
              className={`inline-flex min-w-[120px] items-center justify-center gap-1 rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all ${
                primaryDisabled || primaryLoading
                  ? 'cursor-not-allowed bg-slate-300 opacity-70'
                  : 'bg-gradient-to-r from-teal-500 to-sky-600 hover:from-teal-600 hover:to-sky-700 hover:shadow-lg active:scale-[0.98]'
              }`}
            >
              {primaryLoading ? 'Sending…' : primaryLabel}
              {!primaryLoading && <span aria-hidden>→</span>}
            </SharedButton>
          </footer>
        </div>
        <small className="text-slate-500 text-center flex items-center justify-center mt-4">
          If you have any questions, please contact us at{' '}
          <a href="/Support" className="text-pink hover:underline font-semibold ml-1">
            /Support
          </a>
        </small>
      </div>
    </div>
  );
};

export default FeedbackSurveyLayout;
